
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import Button from '../../../components/base/Button';

export default function MentalHealthPage() {
  const handleConsultationClick = () => {
    window.REACT_APP_NAVIGATE('/consultation');
  };

  const treatments = [
    {
      name: 'Ketamine Therapy',
      description: 'Breakthrough treatment for depression, anxiety, and PTSD',
      benefits: ['Rapid symptom relief', 'Treatment-resistant depression', 'PTSD support', 'Anxiety reduction'],
      approach: 'Medical supervision with personalized dosing protocols'
    },
    {
      name: 'Esketamine Treatment',
      description: 'FDA-approved nasal spray for treatment-resistant depression',
      benefits: ['FDA approved', 'Convenient administration', 'Proven efficacy', 'Supervised treatment'],
      approach: 'In-clinic administration with monitoring'
    },
    {
      name: 'Psychedelic-Assisted Therapy',
      description: 'Guided therapeutic sessions with psychedelic medicines',
      benefits: ['Deep healing', 'Trauma processing', 'Consciousness expansion', 'Therapeutic breakthrough'],
      approach: 'Professional guidance with integration support'
    },
    {
      name: 'Psilocybin Therapy',
      description: 'Mushroom-based therapy for depression and anxiety',
      benefits: ['Natural healing', 'Neuroplasticity', 'Emotional processing', 'Spiritual growth'],
      approach: 'Controlled therapeutic environment'
    },
    {
      name: 'Psychobiotics',
      description: 'Gut-brain axis optimization for mental wellness',
      benefits: ['Mood regulation', 'Anxiety reduction', 'Cognitive enhancement', 'Gut health'],
      approach: 'Personalized probiotic protocols'
    },
    {
      name: 'Genetic Testing',
      description: 'Precision medicine approach to mental health',
      benefits: ['Personalized treatment', 'Medication optimization', 'Genetic insights', 'Targeted therapy'],
      approach: 'Comprehensive genetic analysis'
    }
  ];

  const benefits = [
    {
      icon: 'ri-brain-line',
      title: 'Breakthrough Treatments',
      description: 'Access to cutting-edge therapies like ketamine and psychedelic-assisted healing'
    },
    {
      icon: 'ri-user-heart-line',
      title: 'Personalized Care',
      description: 'Tailored treatment plans based on your unique needs and genetic profile'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Medical Supervision',
      description: 'All treatments conducted under professional medical supervision for safety'
    },
    {
      icon: 'ri-heart-pulse-line',
      title: 'Holistic Approach',
      description: 'Comprehensive mind-body healing that addresses root causes'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#8A4F8A] to-[#C8A2C8] text-white overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://public.readdy.ai/ai/img_res/86f6dafe01541ac1a7ce3b37329ec202.jpg)'
          }}
        ></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-5xl font-bold mb-6">
              Transform Your <span className="text-[#F0F0F0]">Mental Health</span>
            </h1>
            <p className="text-xl mb-8 text-[#F0F0F0]">
              Breakthrough treatments including ketamine therapy, psychedelic-assisted healing, 
              and precision medicine approaches for lasting mental wellness.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="bg-white text-[#8A4F8A] hover:bg-[#F0F0F0]" onClick={handleConsultationClick}>
                <i className="ri-calendar-line mr-2"></i>
                Schedule Consultation
              </Button>
              <Button size="lg" variant="secondary" className="border-white text-white hover:bg-white hover:text-[#8A4F8A]" onClick={() => window.open('tel:202-269-6600', '_self')}>
                <i className="ri-phone-line mr-2"></i>
                Call 202-269-6600
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">
              Why Choose Our <span className="text-[#8A4F8A]">Mental Health Services</span>
            </h2>
            <p className="text-xl text-[#B0B0B0] max-w-3xl mx-auto">
              Innovative treatments that go beyond traditional approaches
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-[#8A4F8A] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${benefit.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-black mb-3">{benefit.title}</h3>
                <p className="text-[#B0B0B0]">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Treatments Section */}
      <section className="py-16 bg-gradient-to-b from-[#F0F0F0] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">
              Our <span className="text-[#8A4F8A]">Treatment Options</span>
            </h2>
            <p className="text-xl text-[#B0B0B0] max-w-3xl mx-auto">
              Comprehensive mental health solutions tailored to your needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {treatments.map((treatment, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8">
                <h3 className="text-xl font-bold text-black mb-4">{treatment.name}</h3>
                <p className="text-[#B0B0B0] mb-6">{treatment.description}</p>
                
                <div className="mb-6">
                  <h4 className="font-semibold text-black mb-3">Benefits:</h4>
                  <ul className="space-y-2">
                    {treatment.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-center text-black text-sm">
                        <i className="ri-check-line text-[#8A4F8A] mr-3"></i>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mb-6">
                  <h4 className="font-semibold text-black mb-2">Approach:</h4>
                  <p className="text-[#B0B0B0] text-sm">{treatment.approach}</p>
                </div>

                <Button className="w-full" onClick={handleConsultationClick}>
                  Learn More
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">
              Your <span className="text-[#8A4F8A]">Healing Journey</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '1', title: 'Initial Assessment', description: 'Comprehensive mental health evaluation and goal setting' },
              { step: '2', title: 'Treatment Planning', description: 'Personalized treatment plan with genetic testing if needed' },
              { step: '3', title: 'Therapy Sessions', description: 'Begin your chosen treatment with medical supervision' },
              { step: '4', title: 'Integration Support', description: 'Ongoing support and therapy integration for lasting results' }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 bg-[#8A4F8A] rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold">{item.step}</span>
                </div>
                <h3 className="text-lg font-bold text-black mb-2">{item.title}</h3>
                <p className="text-[#B0B0B0] text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#8A4F8A] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Transform Your Mental Health?</h2>
          <p className="text-xl mb-8 text-[#F0F0F0]">
            Take the first step towards breakthrough mental wellness
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-[#8A4F8A] hover:bg-[#F0F0F0]" onClick={handleConsultationClick}>
              <i className="ri-calendar-line mr-2"></i>
              Schedule Consultation
            </Button>
            <Button size="lg" variant="secondary" className="border-white text-white hover:bg-white hover:text-[#8A4F8A]" onClick={() => window.open('tel:202-269-6600', '_self')}>
              <i className="ri-phone-line mr-2"></i>
              Call 202-269-6600
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
